import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { CoreModel, CoreSquad } from '../lib/fleet-live-core';
import { orgHref } from '../lib/org';
import { count } from '../lib/time';
import { FleetLiveSquadBody } from './FleetLiveSquadBody';
import { TabList, tabId, tabPanelId } from './TabList';

// The squad feed (#6779, prototype `:3494-3499`, `:3824-3838`): "Squad // <name>" and its
// agents, the squad tabs (the shared `TabList`, at most 8, then "+N more squads"), the repos,
// the role bars and "Live in this squad". Presenter: the Core owns which squad is focused
// (the 8 s cycle moves it) and which repo the Merge Gate acts on.
//
// NO NUMBER WITHOUT A SOURCE: a squad with no bucket shows no agent count and no bars, and
// there is no "Idle" bar (#6777 has no source for an idle agent, brief.md item 71). Each
// repo name is a button that makes that repo the Merge Gate's subject.

export type FleetLiveSquadProps = {
  readonly model: CoreModel;
  readonly focused: number;
  readonly idPrefix: string;
  /** A person chose a squad tab. */
  readonly onSelect: (index: number) => void;
  /** A person chose one of the focused squad's repos for the Merge Gate. */
  readonly onRepo: (squad: CoreSquad, repo: string) => void;
};

function agentsText(n: number): string {
  return tPlural('fleet.live.squad_agents', n, { count: count(n) });
}

export function FleetLiveSquad(props: FleetLiveSquadProps): JSX.Element {
  const squad = (): CoreSquad | undefined => props.model.squads[props.focused];
  return (
    <div class="fleet-live__sq">
      <Show when={squad()}>
        {(s) => (
          <div class="fleet-live__sq-head">
            <h3 class="fleet-live__title">{t('fleet.live.squad_title', { name: s().name })}</h3>
            <Show when={s().total}>
              {(n) => <span class="fleet-live__sq-units">{agentsText(n())}</span>}
            </Show>
          </div>
        )}
      </Show>
      <Show when={props.model.squads.length > 0}>
        <div class="fleet-live__sq-tabs">
          <TabList
            label={t('fleet.live.squads_label')}
            idPrefix={props.idPrefix}
            items={props.model.squads.map((s) => ({ id: s.key, label: s.name }))}
            selected={squad()?.key ?? ''}
            onSelect={(key) => props.onSelect(props.model.squads.findIndex((s) => s.key === key))}
          />
          <Show when={props.model.more > 0}>
            {/* The read names only 8 squads and counts the rest, so the rest are reached
                on the page that lists every squad (a plain anchor the router's link
                delegation takes, as the Repos header's squads link is). */}
            <a class="fleet-live__sq-more" href={orgHref('/account/squads')}>
              {tPlural('fleet.live.squads_more', props.model.more, {
                count: count(props.model.more),
              })}
            </a>
          </Show>
        </div>
      </Show>
      <For each={props.model.squads}>
        {(s) => (
          <div
            role="tabpanel"
            id={tabPanelId(props.idPrefix, s.key)}
            aria-labelledby={tabId(props.idPrefix, s.key)}
            hidden={s.key !== squad()?.key}
            class="fleet-live__sq-panel"
          >
            <Show when={s.key === squad()?.key}>
              <FleetLiveSquadBody squad={s} onRepo={(repo) => props.onRepo(s, repo)} />
            </Show>
          </div>
        )}
      </For>
    </div>
  );
}
